import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/errors';
import { PatientRecordErrorState } from '@/features/patient-record/components/PatientRecordErrorState';
import { PatientRecordPendingState } from '@/features/patient-record/components/PatientRecordPendingState';
import { NonPatientRecordModal } from '@/features/patient-record/components/NonPatientRecordModal';
import { PatientRecordView } from '@/features/patient-record/components/PatientRecordView';
import { patientQueries } from '@/features/patient-record/queries';

import { NotFoundPage } from './NotFoundPage';

/**
 * Route shell for `/patients/:guid`. Resolves the param, runs the patient
 * detail query, and branches to one of four states. Layout, hash sync,
 * and section composition live in `PatientRecordView` and its siblings.
 */
export function PatientPage(): ReactElement {
  const { guid } = useParams<{ guid: string }>();
  const safeGuid = guid ?? '';
  const query = useQuery(patientQueries.detail(safeGuid));

  if (query.isError) {
    if (query.error instanceof ApiError && query.error.code === 'not_found') {
      return <NotFoundPage />;
    }
    return (
      <PatientRecordErrorState
        error={query.error}
        onRetry={() => {
          query.refetch().catch(() => undefined);
        }}
      />
    );
  }

  if (query.isPending) {
    return <PatientRecordPendingState guid={safeGuid} />;
  }

  // A non-patient individual reached directly by URL has no patient record — it only exists as a
  // family member / pedigree node. Cover the page (below the app top bar) with a redirect panel that
  // routes to where it actually lives, instead of the patient record. PTV2-1157.
  if (query.data.personType === 'FAMILY_MEMBER') {
    return <NonPatientRecordModal patient={query.data} />;
  }

  return <PatientRecordView patient={query.data} />;
}
